import React, { useLayoutEffect, useRef } from 'react';
import { WebcamViewport, type WebcamViewportHandle } from '../../scanner/WebcamViewport';
import './PackingPhotoSheet.css';

export const PACKING_PHOTO_PHASES = {
  LIVE: 'live',
  REVIEW: 'review',
} as const;

export type PackingPhotoPhase =
  (typeof PACKING_PHOTO_PHASES)[keyof typeof PACKING_PHOTO_PHASES];

interface PackingPhotoSheetProps {
  readonly title?: string;
  /** Visible heading when it needs markup. `title` stays the dialog name. */
  readonly heading?: React.ReactNode;
  readonly closeLabel?: string;
  readonly phase: PackingPhotoPhase;
  readonly stillSrc: string | null;
  /** Captured frame shown immediately while the JPEG encodes. */
  readonly stillFrame?: HTMLCanvasElement | null;
  readonly submitting: boolean;
  readonly busyLabel?: string;
  readonly error: string;
  readonly viewportRef?: React.Ref<WebcamViewportHandle>;
  /** Replaces the photo viewport. Order-label confirm passes the QR scanner here. */
  readonly camera?: React.ReactNode;
  readonly onClose: () => void;
  readonly onRetake?: () => void;
  readonly onConfirm?: () => void;
}

interface PackingPhotoFrameProps {
  readonly canvas: HTMLCanvasElement;
  readonly pending: boolean;
}

function PackingPhotoFrame({ canvas, pending }: PackingPhotoFrameProps): React.ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    canvas.className = pending
      ? 'packing-photo-still packing-photo-still-pending'
      : 'packing-photo-still';
    host.replaceChildren(canvas);
  }, [canvas, pending]);
  return <div ref={hostRef} className="packing-photo-still-host" />;
}

function PackingPhotoSheet({
  title = 'Photograph packing items',
  heading,
  closeLabel = 'Close packing photo',
  phase,
  stillSrc,
  stillFrame = null,
  submitting,
  busyLabel = 'Saving packing photo',
  error,
  viewportRef,
  camera,
  onClose,
  onRetake = () => undefined,
  onConfirm = () => undefined,
}: PackingPhotoSheetProps): React.ReactElement {
  const reviewing = phase === PACKING_PHOTO_PHASES.REVIEW && (stillFrame != null || stillSrc != null);

  return (
    <div className="packing-photo-sheet" role="dialog" aria-label={title}>
      <div className="packing-photo-topbar">
        <span className="packing-photo-title">{heading ?? title}</span>
        <button
          type="button"
          className="packing-photo-close"
          onClick={onClose}
          aria-label={closeLabel}
        >
          ×
        </button>
      </div>
      <div className="packing-photo-stage">
        {camera != null ? (
          camera
        ) : (
          <WebcamViewport ref={viewportRef} className="packing-photo-webcam" fullResolution />
        )}
        {reviewing && stillFrame != null ? (
          <PackingPhotoFrame canvas={stillFrame} pending={submitting} />
        ) : null}
        {reviewing && stillFrame == null && stillSrc != null ? (
          <img
            src={stillSrc}
            alt="Packing photo"
            className={submitting ? 'packing-photo-still packing-photo-still-pending' : 'packing-photo-still'}
          />
        ) : null}
        {submitting ? (
          <div className="packing-photo-pending">
            <div className="packing-photo-spinner" role="status" aria-label={busyLabel} />
          </div>
        ) : null}
        {error ? (
          <p className="packing-photo-error" role="alert">{error}</p>
        ) : null}
        {reviewing ? (
          <div className="packing-photo-review">
            <button
              type="button"
              className="packing-photo-review-btn packing-photo-retake"
              onClick={onRetake}
              disabled={submitting}
              aria-label="Take another picture"
            >
              ×
            </button>
            <button
              type="button"
              className="packing-photo-review-btn packing-photo-confirm"
              onMouseDown={(event) => event.preventDefault()}
              onClick={onConfirm}
              disabled={submitting}
              aria-label="Submit photograph"
            >
              ✓
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default PackingPhotoSheet;
